import { useEffect, useRef, useState } from 'react';

interface UseStickyHeaderOptions {
  /**
   * The top offset in pixels when the header becomes sticky.
   * Default: 203px (for main view), -16px (for drawer mode)
   */
  topOffset?: number;
  /**
   * Root margin for the IntersectionObserver.
   * Default: '-203px 0px 0px 0px' (for main view), '-16px 0px 0px 0px' (for drawer mode)
   */
  rootMargin?: string;
  /**
   * Z-index for the sticky header.
   * Default: 2 (for main view), 1 (for drawer mode)
   */
  zIndex?: number;
  /**
   * Whether this is in drawer mode (affects default values).
   * Default: false
   */
  isDrawerMode?: boolean;
}

interface UseStickyHeaderReturn {
  /**
   * Ref to attach to the sentinel element (should be placed before the header)
   */
  sentinelRef: React.RefObject<HTMLDivElement>;
  /**
   * Ref to attach to the sticky header element
   */
  stickyHeaderRef: React.RefObject<HTMLDivElement>;
  /**
   * Whether the header is currently sticky
   */
  isHeaderSticky: boolean;
  /**
   * The top offset value (for use in className)
   */
  topOffset: number;
  /**
   * The z-index value (for use in style)
   */
  zIndex: number;

  defaultTopOffset:number;

  alertBarHeight: number;
}

/**
 * Reusable hook for implementing sticky headers with IntersectionObserver.
 *
 * @param options Configuration options for the sticky header
 * @returns Refs and state for managing a sticky header
 *
 * @example
 * ```tsx
 * const { sentinelRef, stickyHeaderRef, isHeaderSticky } = useStickyHeader();
 *
 * return (
 *   <>
 *     <div ref={sentinelRef} className="tw-h-0 tw-w-full tw-mb-[-16px]" />
 *     <div
 *       ref={stickyHeaderRef}
 *       className={isHeaderSticky ? 'tw-sticky tw-top-[203px] ...' : ''}
 *     >
 *       Header content
 *     </div>
 *   </>
 * );
 * ```
 */
export const useStickyHeader = (
  options: UseStickyHeaderOptions = {}
): UseStickyHeaderReturn => {
  const { isDrawerMode = false, topOffset, rootMargin, zIndex } = options;

  // Default values based on drawer mode
  const _alertBarHeight =
    document.body.getElementsByClassName('AlertBar')?.[0]?.clientHeight || 0;
  const defaultTopOffset = isDrawerMode ? -(16 + _alertBarHeight) : 203 + _alertBarHeight;
  const defaultRootMargin = isDrawerMode
    ? `-${16 + _alertBarHeight}px 0px 0px 0px`
    : `${-(_alertBarHeight + 203)}px 0px 0px 0px`;
  const defaultZIndex = isDrawerMode ? 1 : 2;

  const finalTopOffset = topOffset !== undefined ? topOffset : defaultTopOffset;
  const finalRootMargin = rootMargin || defaultRootMargin;

  const finalZIndex = zIndex !== undefined ? zIndex : defaultZIndex;

  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState<boolean>(false);

  useEffect(() => {
    const sentinelElement = sentinelRef.current;
    if (!sentinelElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderSticky(!entry.isIntersecting);
      },
      {
        rootMargin: finalRootMargin,
        threshold: 0,
      }
    );

    observer.observe(sentinelElement);
    return () => observer.unobserve(sentinelElement);
  }, [finalRootMargin]);

  return {
    sentinelRef,
    stickyHeaderRef,
    isHeaderSticky,
    topOffset: finalTopOffset,
    zIndex: finalZIndex,
    defaultTopOffset,
    alertBarHeight: _alertBarHeight,
  };
};
